<template>
    <div class="validator-defi-metrics">
        <!-- DeFi Activity Overview -->
        <div class="section-divider" v-if="!compact">
            <h2 class="section-title">DeFi Activity</h2>
            <p class="section-subtitle">Swap volume, traders served, and DeFi exposure metrics</p>
        </div>

        <div class="analytics-hero mb-4" v-if="defi">
            <div class="analytics-hero-stat">
                <div class="hero-label">Swap Volume</div>
                <div class="hero-value">{{ formatUsd(defi.swapVolume) }}</div>
            </div>
            <div class="analytics-hero-stat hero-stat--green">
                <div class="hero-label">Traders Served</div>
                <div class="hero-value">{{ formatCompact(defi.tradersServed) }}</div>
            </div>
            <div class="analytics-hero-stat hero-stat--amber">
                <div class="hero-label">Avg Swap Size</div>
                <div class="hero-value">{{ formatUsd(defi.avgSwapSize) }}</div>
            </div>
            <div class="analytics-hero-stat hero-stat--blue">
                <div class="hero-label">DeFi Score</div>
                <div class="hero-value">{{ defi.defiExposureScore }}/100</div>
            </div>
        </div>

        <div class="analytics-row cols-2 mb-4" v-if="!compact">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">DeFi Activity Breakdown</div>
                </div>
                <div class="panel-body">
                    <div class="defi-breakdown-grid">
                        <div class="defi-breakdown-item">
                            <div class="breakdown-icon">swap</div>
                            <div class="breakdown-content">
                                <div class="breakdown-label">Swap Transactions</div>
                                <div class="breakdown-value">{{ formatCompact(defi.swapCount) }}</div>
                            </div>
                        </div>
                        <div class="defi-breakdown-item">
                            <div class="breakdown-icon">transfer</div>
                            <div class="breakdown-content">
                                <div class="breakdown-label">Transfer Transactions</div>
                                <div class="breakdown-value">{{ formatCompact(defi.transferCount) }}</div>
                            </div>
                        </div>
                        <div class="defi-breakdown-item">
                            <div class="breakdown-icon">volume</div>
                            <div class="breakdown-content">
                                <div class="breakdown-label">Transfer Volume</div>
                                <div class="breakdown-value">{{ formatUsd(defi.transferVolume) }}</div>
                            </div>
                        </div>
                        <div class="defi-breakdown-item">
                            <div class="breakdown-icon">signers</div>
                            <div class="breakdown-content">
                                <div class="breakdown-label">Unique Signers</div>
                                <div class="breakdown-value">{{ formatCompact(defi.uniqueSigners) }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Block Production Summary</div>
                </div>
                <div class="panel-body">
                    <table class="compass-table">
                        <tbody>
                            <tr>
                                <td>Blocks Produced</td>
                                <td class="text-end">{{ formatCompact(summary.totalBlocks) }}</td>
                            </tr>
                            <tr>
                                <td>Total Transactions</td>
                                <td class="text-end">{{ formatCompact(summary.totalTxns) }}</td>
                            </tr>
                            <tr>
                                <td>Completion Rate</td>
                                <td class="text-end">{{ summary.completionRate }}%</td>
                            </tr>
                            <tr>
                                <td>Avg Txns/Block</td>
                                <td class="text-end">{{ summary.avgTxnsPerBlock }}</td>
                            </tr>
                            <tr>
                                <td>Total Fees</td>
                                <td class="text-end text-gradient">{{ formatSOL(summary.totalFees) }}</td>
                            </tr>
                            <tr>
                                <td>Jito Tips</td>
                                <td class="text-end text-gradient">{{ formatSOL(summary.totalJitoTips) }}</td>
                            </tr>
                            <tr>
                                <td>Jito Transaction %</td>
                                <td class="text-end">{{ summary.jitoPercent }}%</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- Time Series Charts -->
        <div class="analytics-row cols-2 mb-4" v-if="timeSeries.length > 0 && !compact">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Daily Swap Volume</div>
                </div>
                <div class="panel-body">
                    <canvas ref="swapVolumeChart" height="250"></canvas>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Daily Traders Served</div>
                </div>
                <div class="panel-body">
                    <canvas ref="tradersChart" height="250"></canvas>
                </div>
            </div>
        </div>

        <div class="analytics-row cols-2 mb-4" v-if="timeSeries.length > 0 && !compact">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Daily Revenue</div>
                </div>
                <div class="panel-body">
                    <canvas ref="revenueChart" height="250"></canvas>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Completion Rate Over Time</div>
                </div>
                <div class="panel-body">
                    <canvas ref="completionChart" height="250"></canvas>
                </div>
            </div>
        </div>

        <!-- Loading state -->
        <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
        </div>

        <!-- Error state -->
        <div v-if="error" class="alert alert-danger">
            {{ error }}
        </div>
    </div>
</template>

<script>
import Chart from 'chart.js/auto';

export default {
    name: 'ValidatorDefiMetrics',

    props: {
        leader: {
            type: String,
            required: true
        },
        range: {
            type: String,
            default: '7d'
        },
        compact: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            loading: true,
            error: null,
            summary: {},
            defi: {},
            timeSeries: [],
            charts: {}
        };
    },

    mounted() {
        this.loadData();
    },

    beforeUnmount() {
        Object.values(this.charts).forEach(c => c?.destroy());
    },

    watch: {
        leader() {
            this.loadData();
        },
        range() {
            this.loadData();
        }
    },

    methods: {
        async loadData() {
            this.loading = true;
            this.error = null;

            try {
                const res = await fetch(`/analytics/api/validator-defi/${this.leader}?range=${this.range}`);
                if (!res.ok) throw new Error('Failed to load validator data');

                const data = await res.json();
                this.summary = data.summary || {};
                this.defi = data.defi || {};
                this.timeSeries = data.timeSeries || [];

                this.$nextTick(() => {
                    if (!this.compact) {
                        this.initCharts();
                    }
                });
            } catch (e) {
                this.error = e.message;
                console.error('Error loading validator DeFi data:', e);
            } finally {
                this.loading = false;
            }
        },

        initCharts() {
            const ts = this.timeSeries;
            if (ts.length === 0) return;

            const labels = ts.map(d => d.date);
            const commonOpts = {
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: 'index', intersect: false }
            };

            // Swap Volume Chart
            if (this.$refs.swapVolumeChart) {
                if (this.charts.swapVolume) this.charts.swapVolume.destroy();
                this.charts.swapVolume = new Chart(this.$refs.swapVolumeChart, {
                    type: 'bar',
                    data: {
                        labels,
                        datasets: [{
                            label: 'Swap Volume',
                            data: ts.map(d => d.swapVolume),
                            backgroundColor: 'rgba(16, 247, 207, 0.6)',
                            borderColor: '#10f7cf',
                            borderWidth: 1
                        }]
                    },
                    options: {
                        ...commonOpts,
                        scales: {
                            y: {
                                ticks: { callback: v => '$' + this.formatCompact(v) }
                            }
                        }
                    }
                });
            }

            // Traders Chart
            if (this.$refs.tradersChart) {
                if (this.charts.traders) this.charts.traders.destroy();
                this.charts.traders = new Chart(this.$refs.tradersChart, {
                    type: 'line',
                    data: {
                        labels,
                        datasets: [{
                            label: 'Traders Served',
                            data: ts.map(d => d.traders),
                            borderColor: '#8b5cf6',
                            backgroundColor: 'rgba(139, 92, 246, 0.1)',
                            fill: true
                        }]
                    },
                    options: {
                        ...commonOpts,
                        scales: {
                            y: {
                                ticks: { callback: v => this.formatCompact(v) }
                            }
                        }
                    }
                });
            }

            // Revenue Chart
            if (this.$refs.revenueChart) {
                if (this.charts.revenue) this.charts.revenue.destroy();
                this.charts.revenue = new Chart(this.$refs.revenueChart, {
                    type: 'bar',
                    data: {
                        labels,
                        datasets: [
                            {
                                label: 'Fees',
                                data: ts.map(d => d.fees),
                                backgroundColor: 'rgba(99, 102, 241, 0.6)'
                            },
                            {
                                label: 'Jito Tips',
                                data: ts.map(d => d.jitoTips),
                                backgroundColor: 'rgba(249, 115, 22, 0.6)'
                            }
                        ]
                    },
                    options: {
                        ...commonOpts,
                        scales: {
                            x: { stacked: true },
                            y: {
                                stacked: true,
                                title: { display: true, text: 'SOL' }
                            }
                        }
                    }
                });
            }

            // Completion Rate Chart
            if (this.$refs.completionChart) {
                if (this.charts.completion) this.charts.completion.destroy();
                this.charts.completion = new Chart(this.$refs.completionChart, {
                    type: 'line',
                    data: {
                        labels,
                        datasets: [{
                            label: 'Completion Rate',
                            data: ts.map(d => d.completionRate),
                            borderColor: '#22c55e',
                            backgroundColor: 'rgba(34, 197, 94, 0.1)',
                            fill: true
                        }]
                    },
                    options: {
                        ...commonOpts,
                        scales: {
                            y: {
                                title: { display: true, text: '%' }
                            }
                        }
                    }
                });
            }
        },

        formatCompact(v) {
            if (!v) return '0';
            if (v >= 1e9) return (v / 1e9).toFixed(2) + 'B';
            if (v >= 1e6) return (v / 1e6).toFixed(2) + 'M';
            if (v >= 1e3) return (v / 1e3).toFixed(1) + 'K';
            return v.toLocaleString();
        },
        formatUsd(v) {
            if (!v) return '$0';
            if (v >= 1e9) return '$' + (v / 1e9).toFixed(2) + 'B';
            if (v >= 1e6) return '$' + (v / 1e6).toFixed(2) + 'M';
            if (v >= 1e3) return '$' + (v / 1e3).toFixed(1) + 'K';
            return '$' + v.toFixed(2);
        },
        formatSOL(v) {
            if (!v) return '0 SOL';
            if (v >= 1000) return (v / 1000).toFixed(1) + 'K SOL';
            return v.toFixed(2) + ' SOL';
        }
    }
};
</script>

<style scoped>
.defi-breakdown-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
}

.defi-breakdown-item {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 8px;
}

.breakdown-icon {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, var(--compass-cyan), var(--compass-purple));
    border-radius: 8px;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
}

.breakdown-content {
    flex: 1;
}

.breakdown-label {
    font-size: 0.75rem;
    color: var(--compass-text-muted);
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.breakdown-value {
    font-size: 1.25rem;
    font-weight: 600;
    background: linear-gradient(135deg, var(--compass-cyan), var(--compass-purple));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
}
</style>
